import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, IdCard, Loader2, ShieldCheck, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError, fmtDate, openBlob } from "@/lib/api";

const fmtSsn = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 9);
  return [d.slice(0, 3), d.slice(3, 5), d.slice(5)].filter(Boolean).join("-");
};

const SsnBox = ({ info, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [ssn, setSsn] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/identity/ssn", { ssn });
      toast.success("SSN saved securely");
      setSsn("");
      setEdit(false);
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-4" data-testid="ssn-box">
      <p className="flex items-center gap-2 font-semibold"><ShieldCheck className="h-4 w-4 text-blue-700" /> Social Security number</p>
      {info.ssn_on_file && !edit ? (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="font-mono" data-testid="ssn-masked">***-**-{info.ssn_last4}</p>
          <Button size="sm" variant="ghost" onClick={() => setEdit(true)} data-testid="ssn-update-btn">Update</Button>
        </div>
      ) : (
        <div className="mt-2 flex gap-2">
          <Input type="password" autoComplete="off" inputMode="numeric" placeholder="123-45-6789" value={ssn} onChange={(e) => setSsn(fmtSsn(e.target.value))} data-testid="ssn-input" />
          <Button disabled={busy || ssn.replace(/\D/g, "").length !== 9} onClick={save} className="bg-blue-700 text-white hover:bg-blue-800" data-testid="ssn-save-btn">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Save</Button>
        </div>
      )}
      <p className="mt-2 text-xs text-slate-500">Encrypted and visible only to authorized staff. Used to verify your identity.</p>
    </div>
  );
};

export const StateIdBox = ({ info, onChanged, forUserId }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File is too large (10 MB max)");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", "state_id");
    if (forUserId) fd.append("for_user_id", forUserId);
    setBusy(true);
    try {
      await api.post("/documents", fd);
      toast.success("State ID uploaded");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const latest = info.state_ids[0];
  const remove = async () => {
    if (!window.confirm("Remove this state ID?")) return;
    try {
      await api.delete(`/documents/${latest.id}`);
      toast.success("State ID removed");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-4" data-testid="state-id-box">
      <p className="flex items-center gap-2 font-semibold"><IdCard className="h-4 w-4 text-blue-700" /> State ID or driver's license</p>
      {latest && (
        <div className="mt-2 flex items-center gap-3">
          <button className="flex items-center gap-2 text-sm text-blue-700 underline" onClick={() => openBlob(`/documents/${latest.id}/download`, latest.filename).catch(() => toast.error("Could not open file"))} data-testid="state-id-view-btn">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {latest.filename} · {fmtDate(latest.created_at)}
          </button>
          <button className="text-xs text-red-600 hover:underline" onClick={remove} data-testid="state-id-remove-btn">Remove</button>
        </div>
      )}
      <input ref={input} type="file" accept=".pdf,image/*" className="hidden" onChange={upload} data-testid="state-id-file-input" />
      <Button variant="outline" size="sm" className="mt-2" disabled={busy} onClick={() => input.current?.click()} data-testid="state-id-upload-btn">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} {latest ? "Upload a new ID" : "Upload photo or PDF"}
      </Button>
    </div>
  );
};

export const IdentityPanel = () => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/identity").then((r) => setInfo(r.data)).catch(() => {});
  }, []);
  if (!info) return null;
  const missing = !info.ssn_on_file || !info.state_ids.length;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="identity-panel">
      {missing && <p className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="identity-reminder">Please add your Social Security number and a photo of your state ID so we can verify your identity.</p>}
      {!missing && <p className="mb-4 flex items-center gap-2 text-sm text-emerald-700" data-testid="identity-complete"><CheckCircle2 className="h-4 w-4" /> Identity information on file.</p>}
      <div className="grid gap-4 md:grid-cols-2">
        <SsnBox info={info} onSaved={setInfo} />
        <StateIdBox info={info} onChanged={() => api.get("/identity").then((r) => setInfo(r.data))} />
      </div>
    </div>
  );
};
